import { AxiosError } from "axios";
import { LandingLayoutWithLandingDescription } from "PFApp/components/landing_layout";
import { useAppShellStoreActions } from "PFApp/stores/app_shell_store";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useSession } from "PFApp/use_session";
import { Button } from "PFComponents/button";
import { Checkbox } from "PFComponents/checkbox";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useSignUp } from "PFCore/hooks/queries/auth/use_sign_up";
import { SignUpResponse } from "PFCore/services/signup/sign_up";
import { FeatureFlag } from "PFTypes";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { ErrorsAlert } from "./parts/errors_alert";
import css from "./sign_up_page.module.scss";

export const SignUpPage = () => {
  const { showTerms: showTermsModal } = useAppShellStoreActions();
  const { data: currentAccount } = useCurrentAccount();
  const { getSingleErrorByType } = useResponseErrors();

  const navigate = useNavigate();
  const session = useSession();
  const { showGrowl } = useGrowlStoreActions();
  const { mutate: signUp, error, isError, isPending } = useSignUp();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);

  const showTerms = !!currentAccount.customization?.showTermsAndConditions;
  const isRequestAccessEnabled = useIsFeatureEnabled(FeatureFlag.RequestAccess);

  const { emailError, passwordError } = useMemo(() => {
    if (!isError) {
      return {};
    }
    const { response } = error as AxiosError;
    const formattedErrors = getSingleErrorByType(response);

    return {
      emailError: Array.isArray(formattedErrors.email) ? formattedErrors.email[0] : formattedErrors.email,
      passwordError: Array.isArray(formattedErrors.password)
        ? formattedErrors.password[0]
        : formattedErrors.password
    };
  }, [error, getSingleErrorByType, isError]);

  const handleSignupSuccess = (resp: SignUpResponse) => {
    if (resp.requireConfirmation) {
      showGrowl({ message: t("signUpSignIn.willReceiveEmail") });
      return navigate("/");
    }

    return session.signIn(resp.email, password, { onlySuccess: true });
  };

  const handleSignUpClick = () => {
    if (showTerms && !agreed) {
      return;
    }

    signUp(
      {
        accountId: currentAccount.id,
        email,
        password,
        passwordConfirmation: password
      },
      {
        onSuccess: handleSignupSuccess
      }
    );
  };

  const showErrorAlert = passwordError || emailError;

  return (
    <LandingLayoutWithLandingDescription>
      <strong data-qa-id="SignUpPage.header">{t("signUpSignIn.signUp")}</strong>

      <div
        className={css.signUpForm}
        role="form"
        aria-label={t("signUpSignIn.signUp")}
        data-qa-id="SignUpPage.form"
      >
        {showErrorAlert && (
          <ErrorsAlert
            qaId="SignUpPage.error"
            errors={[
              {
                key: "email",
                label: t("emailAddress"),
                error: emailError
                  ? t(`signUpSignIn.api.${emailError}` as unknown as TemplateStringsArray, {
                      defaultValue: emailError
                    })
                  : undefined
              },
              {
                key: "password",
                label: t("password"),
                error: passwordError
              }
            ]}
          />
        )}
        <InputFieldSet
          qaId="SignUpPage.email"
          label={t("emailAddress")}
          inputType="email"
          onChange={setEmail}
        />

        <InputFieldSet
          qaId="SignUpPage.password"
          label={t("password")}
          inputType="password"
          onChange={setPassword}
          showPasswordToggle
        />

        {showTerms && (
          <div className={css.termsAndConditionsContainer} data-qa-id="SignUpPage.agreeToTerms">
            <Checkbox checked={agreed} onChange={setAgreed} />
            <Trans
              i18nKey="signUpSignIn.agreeToTermsAndConditions"
              t={t}
              components={[
                <button className={css.anchorStyledButton} onClick={() => showTermsModal()} key="0" />
              ]}
            ></Trans>
          </div>
        )}
      </div>

      <Button
        kind="primary"
        style={{ width: "100%" }}
        onClick={handleSignUpClick}
        qaId="SignUpPage.signUpButton"
        disabled={email.trim() === "" || password.trim() === "" || (showTerms && !agreed)}
      >
        {isPending ? "..." : t("signUpSignIn.signUp")}
      </Button>

      <div style={{ marginTop: 30, marginBottom: 20 }}>
        {t("signUpSignIn.alreadyHaveAccount")}
        <br />
        <Link to="/" data-qa-id="SignUpPage.logInHereLink">
          {t("signUpSignIn.logInHere")}
        </Link>
      </div>

      {isRequestAccessEnabled && (
        <div style={{ marginBottom: 20 }}>
          {t("signUpSignIn.dontHavePermission")}
          <br />
          <Link to="/request_access" data-qa-id="SignUpPage.requestAccessLink">
            {t("signUpSignIn.requestAccess")}
          </Link>
        </div>
      )}

      <div
        className={css.gdpr}
        role="region"
        aria-label={t("signUpSignIn.personalInformationTitle")}
        data-qa-id="SignUpPage.gdpr"
      >
        <strong>{t("signUpSignIn.personalInformationTitle")}</strong>
        <p>{t("signUpSignIn.personalInformationFirst")}</p>

        <p>
          <Trans
            i18nKey="signUpSignIn.personalInformationSecond"
            t={t}
            components={[
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <a href="https://www.profinda.com/privacy-policy/" target="_blank" rel="noreferrer" key="0" />
            ]}
          />
        </p>

        <p>
          <Trans
            i18nKey="signUpSignIn.personalInformationThird"
            t={t}
            components={[
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <a
                href="https://www.profinda.com/privacy-policy/#your-rights"
                target="_blank"
                rel="noreferrer"
                key="0"
              ></a>
            ]}
          />
        </p>
      </div>
    </LandingLayoutWithLandingDescription>
  );
};
